import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { absoluteImageUrl } from '@/components/admin/ImageUploader';

/**
 * Lightbox
 * A fully accessible full-screen image viewer.
 *
 * Props:
 *  - images: [{ url, alt, caption? }]
 *  - index: number (currently displayed index)
 *  - onIndexChange: (nextIndex) => void
 *  - onClose: () => void
 *
 * Features: keyboard (arrows + Esc), swipe on touch devices, focus trap,
 * body scroll lock, restore focus on close, next/prev preloading only.
 */
export const Lightbox = ({ images, index, onIndexChange, onClose }) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const [entering, setEntering] = useState(true);

  const total = images?.length || 0;
  const current = images?.[index];

  const goPrev = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  const goNext = useCallback(() => {
    if (total <= 1) return;
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  // Body scroll lock + entrance animation
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => setEntering(false), 20);
    // focus close button initially
    window.setTimeout(() => closeBtnRef.current?.focus(), 50);
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = originalOverflow;
      // return focus to previous element
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        try { prev.focus(); } catch { /* noop */ }
      }
    };
  }, []);

  // Keyboard controls + focus trap
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Tab') {
        const focusables = dialogRef.current?.querySelectorAll(
          'button, [href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables?.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, onClose]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0].clientX ?? 0) - touchStartX.current;
    const dy = (e.changedTouches[0].clientY ?? 0) - (touchStartY.current ?? 0);
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) goPrev(); else goNext();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  if (!current) return null;

  // Preload immediate neighbors (helps swipe/keyboard feel instant)
  const prevImg = total > 1 ? images[(index - 1 + total) % total] : null;
  const nextImg = total > 1 ? images[(index + 1) % total] : null;

  const node = (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Image ${index + 1} of ${total}${current.alt ? `: ${current.alt}` : ''}`}
      data-testid="lightbox"
      className={`fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center transition-opacity duration-150 ${
        entering ? 'opacity-0' : 'opacity-100'
      }`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onClick={(e) => {
        // Click on backdrop (outside image) closes
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Preload neighbors (hidden) */}
      {prevImg && <link rel="preload" as="image" href={absoluteImageUrl(prevImg.url)} />}
      {nextImg && <link rel="preload" as="image" href={absoluteImageUrl(nextImg.url)} />}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 sm:px-6 py-3 text-white/90">
        <span className="text-xs sm:text-sm font-medium tracking-wide" data-testid="lightbox-counter">
          {index + 1} of {total}
        </span>
        <button
          ref={closeBtnRef}
          type="button"
          onClick={onClose}
          aria-label="Close (Esc)"
          data-testid="lightbox-close"
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white"
        >
          <X size={18} />
        </button>
      </div>

      {/* Prev / Next controls (visible on desktop; still available on tap-friendly on mobile) */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            aria-label="Previous image (Left arrow)"
            data-testid="lightbox-prev"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            aria-label="Next image (Right arrow)"
            data-testid="lightbox-next"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}

      {/* Image */}
      <figure
        className="max-w-[92vw] max-h-[86vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={current.url}
          src={absoluteImageUrl(current.url)}
          alt={current.alt || `Image ${index + 1}`}
          className="max-w-[92vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
          draggable={false}
        />
        {(current.caption || current.alt) && (
          <figcaption className="mt-3 max-w-[80ch] px-3 text-center text-sm text-white/85 leading-relaxed">
            {current.caption || current.alt}
          </figcaption>
        )}
      </figure>
    </div>
  );

  return createPortal(node, document.body);
};
